/**
 * EditorialSectionShell — the ONE section WRAPPER for every report surface
 * (Editorial Report Redesign v2.0 — "Option-C Monograph"). N1.5C made this the
 * SINGLE section shell across all 11 report types, RETIRING the former
 * `ClassicSectionShell` (which now no longer exists — every call site renders
 * this shell).
 *
 * ── ONE SHELL, TWO MODES ─────────────────────────────────────────────────────
 * The mode is selected by the `editorialActive` prop:
 *
 *   • editorialActive = false (or absent) → the **classic** treatment. The shell
 *     renders EXACTLY what the retired ClassicSectionShell rendered: a single
 *     `<div className={shellClass} style={shellStyle} {...rest}>{children}</div>`
 *     with the same combined section padding / density class / unified 14px body
 *     font / tighter body leading / `display:none` omission. It adds NOTHING else
 *     — no editorial opener, no `data-editorial-section`. The caller still owns
 *     its own `<h2>` heading (rendered inside `children`). This path is
 *     BYTE-IDENTICAL to the old ClassicSectionShell DOM/classes, so every
 *     flag-OFF report (all 11 types) renders unchanged.
 *
 *   • editorialActive = true → the **editorial** treatment. The shell renders the
 *     full magazine OPENER above the body: a small Hanken APPARATUS eyebrow
 *     ("01 · SECTION", uppercase + teal + wide-tracked, middle-dot separator), the
 *     Fraunces display heading (the `<h2 data-heading="true">` is preserved so the
 *     TOC anchor / section-number stamp / in-page nav keep working), an optional
 *     italic standfirst lede, and a 1px hairline rule under the head — all in OUR
 *     theme tokens + fonts. It stamps
 *     `data-editorial-section="true"` so the report-root `[data-editorial]`
 *     drop-cap / pull-quote / `data-flow` CSS hooks fire over the body. The
 *     caller passes `heading` (and optionally `standfirst` / `sectionNumber`) and
 *     STOPS rendering its own `<h2>` — the shell owns the opener.
 *
 * The CONTROL flow (padding / density / body font / display-none omission) is
 * shared and identical between the two modes; the editorial opener is the only
 * delta. So flipping the flag never changes geometry, only the section masthead.
 *
 * The section body (provenance strip, meta-leak chip, the section component) is
 * passed as `children` and rendered VERBATIM below the opener — the shell never
 * clones or restyles it, so the section's existing content (every table / chart
 * / KPI / finding / citation) renders unchanged: zero data loss, no citation
 * reindex.
 *
 * `data-flow` CLASSIFICATION CONTRACT (the N2 2-col enabler): in editorial mode
 * the shell stamps `data-editorial-section="true"` so the section components can,
 * inside, mark each body block `data-flow="flow"` (running prose — will columnise
 * in N2) or `data-flow="span"` (an exhibit — table / chart / KPI-rail / datasheet
 * / matrix / map — will `column-span:all` in N2). The shell does NOT classify
 * children itself (it cannot see into them); it provides the editorial frame +
 * the `[data-editorial]` scope under which those rules fire.
 *
 * Pure / print-safe (no shadow, no animation) — identical on-screen and in the
 * measured-PDF deck (the editorial CSS lives in the shared BASE stylesheet, not
 * an @media-print block, so view == PDF by construction).
 */
import React from 'react';
import type { CSSProperties } from 'react';
import type { ReportTheme } from '../../../types/reportTheme';
import { roleColor } from '../../../lib/reportRoleColors';
import {
  reportSectionPaddingClass,
  reportShellPaddingClass,
} from '../../../lib/reportSectionChrome';
import {
  formatSectionNumber,
  reportNumberedHeadingStyles,
  reportTypographySizes,
  reportShellBodyFontSize,
  REPORT_LINE_HEIGHT,
} from '../../../lib/reportSectionHeading';

/**
 * The hero numeral + standfirst use the section's own heading face. The report
 * root (viewer SiteRagReportView + the PDF deck) both set `--heading-font`, so
 * this is print-safe in both render paths; falls back to the body font then
 * inherit when the var is absent (a unit-test mount with no report root).
 * Byte-identical to EditorialSectionLayout / EditorialHeroNumeral.
 */
const SERIF = 'var(--heading-font, var(--report-body-font, inherit))';

/** True when a node will actually render something (mirrors the editorial primitives). */
function isRenderable(node: React.ReactNode): boolean {
  if (node == null || node === false) return false;
  if (typeof node === 'string') return node.trim().length > 0;
  return true;
}

export interface EditorialSectionShellProps
  extends React.HTMLAttributes<HTMLDivElement> {
  theme: ReportTheme;
  /**
   * Selects the mode. `false`/absent → classic treatment (byte-identical to the
   * retired ClassicSectionShell — children-only, no opener, no marker). `true` →
   * editorial treatment (renders the opener; `heading` is required and the caller
   * stops rendering its own `<h2>`).
   */
  editorialActive?: boolean;
  /** 1-based section index for the hero numeral + "NN" head index; out-of-range ⇒ no numeral. (Editorial mode only.) */
  sectionNumber?: number;
  /** The section heading node — typically an `<EditableHeading>` or the label string. (Editorial mode only.) */
  heading?: React.ReactNode;
  /**
   * Optional SHORT section-name label for the apparatus eyebrow, rendered after
   * the number + a middle-dot ("01 · EXECUTIVE SUMMARY"). When absent the eyebrow
   * shows the number alone — the section name already reads as the Fraunces `<h2>`
   * headline below, so callers only pass this where a label DISTINCT from the
   * headline is wanted (e.g. a future generated-headline section). (Editorial mode only.)
   */
  eyebrowLabel?: React.ReactNode;
  /** Optional editorial lede above the body (serif italic, only when supplied). (Editorial mode only.) */
  standfirst?: React.ReactNode;
  /**
   * WAVE 3 — when true the standfirst is a GENERATED editorial thesis (a
   * conclusion-first claim), so it renders as the option-C `.thesis`: an accent
   * left-rule + slate ink (one step bolder than the muted deterministic lede).
   * Default false keeps the existing muted deterministic-lede styling so the
   * flag-OFF / no-layer render is BYTE-IDENTICAL. (Editorial mode only.)
   */
  standfirstIsThesis?: boolean;
  /**
   * AI LAYOUT/DESIGN PASS (report-ai-design-pass · Phase 3) — when `true` the
   * layout plan requested a HERO opener for this section, stamping
   * `data-editorial-hero="true"` on the editorial section wrapper so the
   * report-root editorial CSS can elevate the opener (larger numeral / drop-cap).
   * Default `false`/absent ⇒ NO attribute, BYTE-IDENTICAL to today. Editorial
   * mode only — the classic mode early-returns before the marker is stamped.
   */
  heroEmphasis?: boolean;
  /** Retained for call-site compatibility; page-level chrome owns the rail. */
  omitSectionSidebar?: boolean;
  /** When false, skip per-section shell padding (section lives inside a padded page wrapper). */
  shellPadding?: boolean;
  /** Extra shell classes merged after padding + relative (e.g. the inline section chrome). */
  extraClassName?: string;
  /** The section body — provenance, meta-leak chip, the section component. Rendered verbatim. */
  children: React.ReactNode;
}

export const EditorialSectionShell: React.FC<EditorialSectionShellProps> = ({
  theme,
  editorialActive = false,
  sectionNumber,
  heading,
  eyebrowLabel,
  standfirst,
  standfirstIsThesis = false,
  heroEmphasis = false,
  omitSectionSidebar = false,
  shellPadding = true,
  extraClassName,
  className,
  children,
  style,
  ...rest
}) => {
  // PHASE-D — true omission of hidden sections (identical to the retired
  // ClassicSectionShell): a not-visible section is passed `style={{ display:
  // 'none' }}` and must OMIT entirely (no shell, no heading, no gap) so the
  // column/flex flow closes up and the TOC + figure numbering stay in lockstep
  // with the body.
  if (style?.display === 'none') {
    return null;
  }

  // P2.4 — on-screen density. `comfortable` (the default) adds NOTHING so legacy
  // reports keep their exact current spacing (byte-stable); `compact` tightens
  // the section's vertical rhythm via a scoped utility (see the
  // `[data-section-density="compact"]` rule in index.css). Never touches the
  // horizontal page gutter (page chrome), only the section's own padding.
  const dense = theme.density === 'compact';
  const densityClass = dense ? 'report-density-compact' : '';

  const shellClass = [
    shellPadding
      ? omitSectionSidebar
        ? reportShellPaddingClass(true)
        : reportSectionPaddingClass(false)
      : '',
    'relative',
    densityClass,
    extraClassName,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // TS3 + TS8 + K1 — scale this section's body text to the active theme via the
  // ONE shared report type scale, and tighten leading to the redesign's body
  // rhythm (1.5). Inline `fontSize` so it overrides any inherited Tailwind size
  // while remaining mergeable with a caller-supplied `style` (which wins).
  // Byte-identical to the retired ClassicSectionShell.
  const shellStyle: CSSProperties = {
    fontSize: reportShellBodyFontSize(theme),
    lineHeight: REPORT_LINE_HEIGHT.body,
    ...style,
  };

  // ── CLASSIC MODE (flag OFF) — byte-identical to the retired ClassicSectionShell.
  // Just the shell wrapper around the caller's children (which include the
  // caller's own <h2>). No opener, no `data-editorial-section`. Any
  // `data-editorial-section` the caller explicitly passes flows through `...rest`.
  if (!editorialActive) {
    return (
      <div className={shellClass} style={shellStyle} {...rest}>
        {children}
      </div>
    );
  }

  // ── EDITORIAL MODE (flag ON) — render the magazine opener above the body.
  // Resolve the numbered-rhythm tokens in OUR fonts + theme inks (the lib stays
  // roleColor-free for cycle avoidance, so pass the resolved inks).
  const numbered = reportNumberedHeadingStyles(theme, {
    accent: roleColor(theme, 'accent'),
    ink: roleColor(theme, 'ink'),
    muted: roleColor(theme, 'muted'),
  });
  const sizes = reportTypographySizes(theme);
  const padded = formatSectionNumber(sectionNumber ?? NaN); // "01" / "12" / '' if invalid
  // The apparatus eyebrow renders when EITHER a valid number OR a distinct label
  // exists; otherwise (e.g. an appendix with no index + no label) the head opens
  // straight on the Fraunces <h2>.
  const hasEyebrow = padded !== '' || isRenderable(eyebrowLabel);

  // AI LAYOUT PASS (emphasis) — hero opener marker, stamped ONLY when the layout
  // plan requested it for this section. Absent ⇒ `{}` (no attribute), so the
  // editorial wrapper is byte-identical to today.
  const heroAttrs = heroEmphasis ? { 'data-editorial-hero': 'true' as const } : {};

  return (
    <div
      className={shellClass}
      style={shellStyle}
      data-editorial-section="true"
      {...heroAttrs}
      {...rest}
    >
      {/* ── Editorial opener (the magazine masthead rhythm for the section) ──
          P-GROUP Phase A — `data-layout-group="opener"` makes the whole opener
          (numeral eyebrow + Fraunces h2 + standfirst + hairline rule) ONE
          fragmentation atom in the paged deck (`break-inside: avoid`, which
          pagedjs honours — unlike the break-after:avoid the density CSS relies
          on for the Chromium fallback). Kills the live "bare 05 numeral at the
          page foot, its title overleaf" class at LAYOUT time; the deOrphan /
          orphan-collector passes remain as backstops. */}
      <header
        data-editorial-opener="true"
        data-layout-group="opener"
        style={{ margin: `0 0 ${dense ? 20 : 28}px` }}
      >
        {/* (1) small Hanken APPARATUS eyebrow — "01 · SECTION" (uppercase, teal,
            wide-tracked, middle-dot separator). Replaces the former big standalone
            serif hero-numeral (William house-style 2026-06-29). The NUMBER keeps
            `data-editorial-numeral` and its textContent is the bare zero-padded
            index so the de-orphan / page-measure hooks + pins stay valid; an
            optional `eyebrowLabel` renders after the dot. The whole kicker is
            aria-hidden (decorative) so the <h2> stays the section's a11y name. */}
        {hasEyebrow && (
          <div
            aria-hidden="true"
            data-editorial-eyebrow="true"
            // WALL 4 (2026-08-18) — the tagged-PDF provenance audit
            // (pdfRenderCore) requires every aria-hidden text run to ALSO
            // carry data-pdf-decorative-text, or its glyphs surface as
            // "unmarked text lacking decorative provenance". This
            // wide-tracked "01" eyebrow was emitted by pdf.js as "0 1" on
            // every numbered section of the Pronghorn exec-summary and
            // failed the artifact stage (v5). Marking it decorative lets the
            // observation collector record it, so the audit proves it.
            data-pdf-decorative-text
            style={{ ...numbered.number, marginBottom: dense ? 6 : 8 }}
          >
            {padded !== '' && (
              <span data-editorial-numeral="true" aria-hidden="true">{padded}</span>
            )}
            {padded !== '' && isRenderable(eyebrowLabel) && (
              <span style={{ margin: '0 0.45em', opacity: 0.65 }}>·</span>
            )}
            {isRenderable(eyebrowLabel) && (
              <span data-editorial-eyebrow-label="true">{eyebrowLabel}</span>
            )}
          </div>
        )}
        {/* (2) the Fraunces display headline. The <h2 data-heading="true"> is
            preserved so the TOC anchor / section number / in-page nav keep
            working — the section name lives HERE (the apparatus eyebrow above
            carries the number, and optionally a distinct short label).

            SINGLE HEAD RULE (Redhead QA, 2026-07-02) — the print/deck density
            layer forces `border-bottom-width: 1px !important` on every
            `[data-heading="true"]` (reportDensityCss, thinning the CLASSIC
            `border-b-2` h2s), and Tailwind preflight's global
            `border-style: solid` made that width PAINT a phantom rule tight
            under this h2 — which never declared a border — stacking a SECOND
            line above the deliberate `data-editorial-headrule` below. An
            explicit inline `borderBottomStyle: 'none'` defeats both (the
            density rule only sets the WIDTH; with style `none` nothing
            paints), so the numbered opener draws exactly ONE rule on every
            surface. Classic-mode h2s (caller-rendered, `border-b-2`) are
            untouched. */}
        <h2
          data-heading="true"
          style={{
            ...numbered.heading,
            marginTop: hasEyebrow ? (dense ? 4 : 6) : 0,
            borderBottomStyle: 'none',
          }}
        >
          {heading}
        </h2>
        {/* (3) standfirst lede — italic serif, only when supplied. The
            DETERMINISTIC lede is muted (byte-identical to today); a GENERATED
            THESIS takes the option-C `.thesis` treatment: an accent left-rule +
            slate ink so the section conclusion reads up front and decisive. */}
        {isRenderable(standfirst) && (
          <p
            data-editorial-standfirst="true"
            data-editorial-thesis={standfirstIsThesis ? 'true' : undefined}
            style={{
              fontFamily: SERIF,
              fontStyle: 'italic',
              fontSize: sizes.h3,
              lineHeight: REPORT_LINE_HEIGHT.body,
              fontWeight: 400,
              color: roleColor(theme, standfirstIsThesis ? 'body' : 'muted'),
              margin: `${dense ? 10 : 14}px 0 0`,
              maxWidth: standfirstIsThesis ? '50em' : '46em',
              // William QA 2026-06-28 — left accent rule removed report-wide
            }}
          >
            {standfirst}
          </p>
        )}
        {/* (4) 1px hairline rule under the headline / thesis — the academic-
            editorial section hair (William house-style 2026-06-29, re-adding the
            rule the 06-28 QA had stripped). Decorative, theme `rule` ink. */}
        <div
          aria-hidden="true"
          data-editorial-headrule="true"
          style={{
            height: 1,
            background: roleColor(theme, 'rule'),
            margin: `${dense ? 12 : 16}px 0 0`,
          }}
        />
      </header>
      {/* (5) the section body — rendered VERBATIM below the opener. The flowing
          editorial PROSE BODY (EditorialProseBody) is emitted by the section
          components themselves (where they can also suppress their now-redundant
          structured prose blocks), so the shell stays prose-agnostic. */}
      {children}
    </div>
  );
};
